import React, { useState } from 'react';
import { colors } from '../styles/colors';
import { Lock, Copy, Check } from 'lucide-react';

/**
 * ContractAddressCard - Displays token contract address with reveal/copy functionality
 * 
 * @param {string} address - The full contract address
 * @param {string} theme - 'gold' for AZG or 'silver' for AZS
 */
const ContractAddressCard = ({ address, theme = 'gold' }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Theme colors
  const themeColors = theme === 'gold' 
    ? { accent: '#C6A84B', highlight: '#F2D675', border: '#C6A84B40' }
    : { accent: '#B8BCC2', highlight: '#EDEDED', border: '#B8BCC240' };

  // Truncate address for display
  const truncatedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleReveal = () => {
    setIsRevealed(true);
  };

  return (
    <div
      className="backdrop-blur-sm border rounded-xl transition-all duration-300"
      style={{ 
        backgroundColor: `${colors.white}06`,
        borderColor: themeColors.border,
      }}
    >
      {/* Header */}
      <div className="p-4 sm:p-5">
        <h3 
          className="text-sm font-medium mb-3"
          style={{ color: colors.white, opacity: 0.6 }}
        >
          Token Contract Address
        </h3>

        {/* Collapsed State - Unlock Button */}
        {!isRevealed && (
          <button
            onClick={handleReveal}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border transition-all duration-300 hover:bg-white/5"
            style={{ 
              borderColor: `${themeColors.accent}50`,
              color: themeColors.highlight,
            }}
          >
            <Lock size={16} style={{ opacity: 0.8 }} />
            <span className="text-sm font-medium">Unlock contract address</span>
          </button>
        )}

        {/* Expanded State - Address Display */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-out ${
            isRevealed ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div 
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg"
            style={{ backgroundColor: `${colors.white}05` }}
          >
            {/* Address - Full on desktop, truncated on mobile */}
            <div className="flex-1 min-w-0">
              {/* Desktop: Full address */}
              <code 
                className="hidden sm:block text-sm font-mono break-all"
                style={{ color: themeColors.highlight }}
              >
                {address}
              </code>
              {/* Mobile: Truncated address */}
              <code 
                className="block sm:hidden text-sm font-mono"
                style={{ color: themeColors.highlight }}
              >
                {truncatedAddress}
              </code>
            </div>

            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg border transition-all duration-200 hover:bg-white/5 flex-shrink-0"
              style={{ 
                borderColor: `${colors.white}20`,
                color: copied ? '#4ade80' : colors.white,
                minWidth: '90px'
              }}
            >
              {copied ? (
                <>
                  <Check size={14} />
                  <span className="text-xs font-medium">Copied</span>
                </>
              ) : (
                <>
                  <Copy size={14} style={{ opacity: 0.7 }} />
                  <span className="text-xs font-medium" style={{ opacity: 0.7 }}>Copy</span>
                </>
              )}
            </button>
          </div>

          {/* Mobile: Full address tooltip */}
          <p 
            className="sm:hidden text-xs mt-2 px-1"
            style={{ color: colors.white, opacity: 0.4 }}
          >
            Tap copy to copy full address
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContractAddressCard;
